import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { sessionIdSelector } from 'store/slices/global';

// ─── Utilities ───────────────────────────────────────────────────────────────

/**
 * Safely extracts string value from query param (handles arrays)
 */
const safeStringValue = (value: string | string[] | null): string => {
  if (!value) return '';
  if (Array.isArray(value)) return value[0] || '';
  return value;
};

const getSessionIdFromSearch = (search: string): string => {
  const sp = new URLSearchParams(search);
  return (
    safeStringValue(sp.get('session_id')) ||
    safeStringValue(sp.get('cashier_session_id')) ||
    safeStringValue(sp.get('sessionId')) ||
    ''
  );
};

export const resolveSessionFromUrl = (): string =>
  typeof window === 'undefined' ? '' : getSessionIdFromSearch(window.location.search);

/**
 * In-memory session cache — survives URL changes within the same tab lifecycle.
 * Populated on first successful resolution from URL or store.
 */
let cachedSessionId = '';

export const cacheSessionId = (id: string): void => {
  if (id) {
    cachedSessionId = id;
  }
};

/**
 * Universal sessionId resolver with fallback chain
 * Priority: URL params → provided fallback (store) → in-memory cache
 */
export const getSessionId = (fallback?: string): string => {
  const fromUrl = resolveSessionFromUrl();
  if (fromUrl) {
    cacheSessionId(fromUrl);
    return fromUrl;
  }
  const resolved = fallback || cachedSessionId || '';
  if (resolved) {
    cacheSessionId(resolved);
  }
  return resolved;
};

// ─── React Hook ──────────────────────────────────────────────────────────────

export const useGetSessionId = (): string => {
  const [searchParams] = useSearchParams();
  const storedSessionId = useAppSelector(sessionIdSelector);
  const sessionIdFromUrl =
    searchParams.get('session_id') || searchParams.get('cashier_session_id') || searchParams.get('sessionId') || '';
  const sessionId = getSessionId(storedSessionId || sessionIdFromUrl);

  useEffect(() => {
    if (sessionId) {
      cacheSessionId(sessionId);
    }
  }, [sessionId]);

  return sessionId;
};
